import { Literal, Static, Union } from "runtypes";

import { DataConnectionTypeLiteral } from "../dataConnectionTypes.js";
import { assertNever } from "../errors.js";
import { HqlAggregationFunction } from "../hql/types.js";
import { getNormalEnum } from "../runtypeEnums.js";
import { Dialect } from "../semantic-layer/generated/hex_sl_schema.js";

export const SqlDialectLiteral = Union(
  ...DataConnectionTypeLiteral.alternatives,
  Literal("duckdb"),
);
export type SqlDialect = Static<typeof SqlDialectLiteral>;
export const SqlDialect = getNormalEnum(SqlDialectLiteral);
export type PushdownSqlDialect = Exclude<SqlDialect, "transform">;

export const sqlDialectToHexSLDialect = (dialect: SqlDialect): Dialect => {
  if (
    dialect === SqlDialect.cloudsql ||
    dialect === SqlDialect.rds ||
    dialect === SqlDialect.transform
  ) {
    throw new Error(`${dialect} is not supported in HexSL`);
  }

  switch (dialect) {
    case SqlDialect.motherduck:
      return "duckdb";
    case SqlDialect.sqlserver:
    case SqlDialect.cloudsql__sqlserver:
      return "mssql";
    case SqlDialect.mariadb:
    case SqlDialect.cloudsql__mysql:
    case SqlDialect.rds__mysql:
      return "mysql";
    case SqlDialect.cloudsql__postgres:
    case SqlDialect.rds__postgres:
    case SqlDialect.dremio:
    case SqlDialect.materialize:
      return "postgres";
    default:
      return dialect;
  }
};

const SUPPORTS_SQLGEN = {
  alloydb: true,
  athena: true,
  bigquery: true,
  clickhouse: true,
  cloudsql__mysql: true,
  cloudsql__postgres: true,
  databricks: true,
  duckdb: true,
  mariadb: true,
  motherduck: true,
  mysql: true,
  postgres: true,
  rds__mysql: true,
  rds__postgres: true,
  redshift: true,
  snowflake: true,

  cloudsql: false,
  cloudsql__sqlserver: false,
  dremio: false,
  materialize: false,
  prestodb: false,
  rds: false,
  spark: false,
  sqlserver: false,
  starburst: false,
  transform: false,
  trino: false,
} satisfies Record<SqlDialect, boolean>;

type GetTrueKeys<T> = keyof {
  [K in keyof T as T[K] extends true ? K : never]: true;
};
export type SqlGenDialect = GetTrueKeys<typeof SUPPORTS_SQLGEN>;

export const isSqlGenDialect = (
  dialect: SqlDialect,
): dialect is SqlGenDialect => SUPPORTS_SQLGEN[dialect];

/**
 * This error type should be thrown from pushdown SQL generation to indicate
 * known/allowed gaps in functionality due to SQL dialect differences. For
 * example, this error is thrown for the median aggregation with MySQL/MariaDB
 * since they do not support computing medians.
 */
export class DialectUnsupportedError extends Error {}

export const QUOTING_CHARACTERS_REGEX = /["\[\]`]/g;

// Be sure to keep this in sync with `hex.sql.dialects`
export function getQuotingCharacters(dialect: SqlDialect): [string, string] {
  switch (dialect) {
    case "postgres":
    case "cloudsql":
    case "cloudsql__postgres":
    case "rds":
    case "rds__postgres":
    case "prestodb":
    case "snowflake":
    case "athena":
    case "clickhouse":
    case "dremio":
    case "redshift":
    case "transform":
    case "trino":
    case "duckdb":
    case "alloydb":
    case "starburst":
    case "materialize":
    case "motherduck":
      return [`"`, `"`];
    case "bigquery":
    case "databricks":
    case "mariadb":
    case "mysql":
    case "cloudsql__mysql":
    case "rds__mysql":
    case "spark":
      return ["`", "`"];
    case "sqlserver":
    case "cloudsql__sqlserver":
      return ["[", "]"];
    default:
      assertNever(dialect, dialect);
  }
}

export function quoteSqlIdentifier(
  identifier: string,
  dialect: SqlDialect,
): string {
  const [openQuote, closeQuote] = getQuotingCharacters(dialect);
  // SQL delimited identifiers escape an embedded closing delimiter by doubling
  // it: "a""b", `a``b`, and SQL Server's [a]]b]. The opening delimiter only
  // matters when it is also the closing delimiter.
  const escapedIdentifier = identifier.replaceAll(
    closeQuote,
    closeQuote.repeat(2),
  );
  return `${openQuote}${escapedIdentifier}${closeQuote}`;
}

export function sqlGenSupportsAggForDialect(
  agg: HqlAggregationFunction,
  dialect: SqlDialect,
): boolean {
  switch (dialect) {
    // Dialects that support all aggregate functions
    case "clickhouse":
    case "databricks":
    case "duckdb":
    case "motherduck":
    case "postgres":
    case "cloudsql__postgres":
    case "rds__postgres":
    case "alloydb":
    case "snowflake":
      return true;

    // Dialects that support all except for median
    case "athena":
    case "bigquery":
    case "mysql":
    case "cloudsql__mysql":
    case "rds__mysql":
    case "mariadb":
    case "redshift":
      return agg !== "Median";

    // Dialects not support by sql-gen
    default:
      return false;
  }
}
